// BUSCA AVANÇADA do hero "Aurora" (busca 13) — painel de filtros no visual novo (branco).
//
// Abre pelo botão "Busca avançada" da barra: no desktop é um MODAL centralizado (cartão branco
// de raio 24 sobre um véu escuro); no celular é TELA CHEIA com o "Buscar imóveis" fixo no rodapé.
// A casca segue a receita da `welcome3/FiltrosModalMobile` (portal no <body> — o cartão do hero
// pode virar containing block por transform —, Esc fecha, o fundo para de rolar, o foco entra no
// botão de fechar, rodapé Limpar/Buscar); os FILTROS são os clássicos da busca 12
// (`welcome3/WelcomePieces`): bairro(s) da cidade escolhida, preço mín/máx, área mín/máx, quartos,
// suítes, banheiros, vagas, categoria (site.categoria_home — lista suspensa de UMA escolha, com "Todas";
// Daniel 14/10), data de entrega (site.mostrar_data_entrega), empreendimento
// (site.exibir_empreendimento, lista `allCompany` do hero) e código do imóvel.
//
// O painel COMPLEMENTA a barra: transação, cidade, tipo e quartos vêm do estado do hook
// (useHeroSearch) e entram na URL; "quartos" é o MESMO estado da barra (setDormitorios). A URL é
// a que a listagem /imoveis entende (o renderPath da busca 12):
//   /imoveis/<transação>/<cidade>/<bairro1+bairro2>/<empreendimento>/<tipo>[/<código>]
//   ?min&max&area_min&area_max&dormitorios&banheiros&suites&vagas&categoria&data_entrega
// (características ficaram FORA do painel — Daniel 14/10: "característica tira fora" — e do contador.)
// Quartos/suítes/banheiros/vagas marcam VÁRIOS números (Daniel 20/10, como a lateral de /imoveis):
// o valor é o texto "2,3" (utils/qtdMultiSelecao) e a URL leva `dormitorios=2,3`.
// Cidade e tipo da barra também marcam VÁRIOS (Daniel 21/10, utils/buscaMultiSelecao): os bairros
// listados são os de todas as cidades marcadas (useBairrosDasCidades) e, com 2+ cidades, o caminho
// fica sem cidade e a URL leva `?cidades=<ids>`. No Larissa (o "Onde?" escolhe um lugar só) as
// cidades se marcam AQUI, no grupo "Cidades" (`cidadesNoPainel`), sobre o mesmo estado da barra.
// e passa pelo mesmo `finalize` do hook (codsite, filial ativa, acentos, minúsculas).
//
// O componente fica MONTADO com o painel fechado (só o portal some): os filtros escolhidos e as
// listas carregadas sobrevivem ao fechar/reabrir, e o pai mostra a contagem no botão (onContagem).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CIDADES_QUERY_PARAM } from '../../../lib/region';
import { allDatasEntrega, categorias } from '../../../services/selectors';
import { normalizaTermo } from '../../../utils/buscaLocal';
import { opcoesDoTexto, resumoMarcados } from '../../../utils/buscaMultiSelecao';
import { formatSelects, formatSelectsTransacao } from '../../../utils/FunctionsGlobals';
import { alternarQtdTexto, QTD_NUMEROS, qtdListaDoTexto, qtdTextoParaUrl, virgulaCrua } from '../../../utils/qtdMultiSelecao';
import CampoSelecao from '../../shared/hero-search/CampoSelecao';
import CampoSelecaoMulti from '../../shared/hero-search/CampoSelecaoMulti';
import { encodeSegment } from '../../shared/hero-search/useHeroSearch';
import { useBairrosDasCidades } from '../../shared/multi-selecao/useBairrosDasCidades';
import styles from './BuscaAvancadaAurora.module.css';

export interface OpcaoFiltro {
    label: string;
    value: any;
}

export interface FiltrosAvancados {
    bairros: OpcaoFiltro[];
    precoMin: string;
    precoMax: string;
    areaMin: string;
    areaMax: string;
    suites: string;
    banheiros: string;
    vagas: string;
    categoria: string;
    datasEntrega: string[];
    empreendimento: OpcaoFiltro | null;
    codigo: string;
}

export const FILTROS_VAZIOS: FiltrosAvancados = {
    bairros: [],
    precoMin: '',
    precoMax: '',
    areaMin: '',
    areaMax: '',
    suites: '',
    banheiros: '',
    vagas: '',
    categoria: '',
    datasEntrega: [],
    empreendimento: null,
    codigo: '',
};

export interface BasicoDaBarra {
    transacao: string;
    /** id(s) de cidade da barra — "12,7" com várias marcadas. */
    cidade: string;
    /** id(s) de tipo da barra — "3,5" com vários marcados. */
    tipo: string;
    dormitorios: string;
}

interface Props {
    aberto: boolean;
    onFechar: () => void;
    site: any;
    origin?: any;
    codsite?: any;
    allCities?: OpcaoFiltro[];
    allTypes?: OpcaoFiltro[];
    allCompany?: OpcaoFiltro[];
    basico: BasicoDaBarra;
    setDormitorios: (_valor: string) => void;
    /** Larissa: o grupo "Cidades" (marcar várias) no painel, sobre o `setCidade` do hook. */
    cidadesNoPainel?: boolean;
    setCidade?: (_valor: string) => void;
    /** o `finalize` do useHeroSearch: codsite + filial ativa + acentos + minúsculas. */
    finalize: (_path: string) => string;
    onBuscar: (_url: string) => void;
    onContagem?: (_quantidade: number) => void;
    corAcao: string;
    corAcaoTexto: string;
    corTexto: string;
    /** Pele de outro modelo (14/10): 'horizonte' só acrescenta a classe .horizonte na casca (raios, tipografia e
     *  campos do modelo 8); filtros, URL e comportamento são os mesmos. Sem a prop, o painel do Aurora de sempre.
     *  'larissa' (15/10): idem com a pele do modelo 11 (classe .larissa — cartão claro, título serifado, raios de 8).
     *  'internacional' (21/10): pele do modelo 12 (classe .internacional — Montserrat, título 800, tudo em pílula,
     *  números em círculo, botão sólido na base do tenant). */
    variante?: 'horizonte' | 'larissa' | 'internacional';
}

const soNumero = (valor: string) => parseInt(String(valor || '').replace(/\D/g, ''), 10) || 0;
const formataMilhar = (valor: string) => {
    const n = soNumero(valor);
    return n > 0 ? n.toLocaleString('pt-BR') : '';
};

/** Monta a URL de /imoveis com a barra + os filtros avançados (contrato da listagem). */
export const montarUrlAvancada = (
    basico: BasicoDaBarra,
    filtros: FiltrosAvancados,
    allCities: OpcaoFiltro[] | undefined,
    allTypes: OpcaoFiltro[] | undefined,
    finalize: (_path: string) => string,
    codsite?: unknown
): string => {
    const cidades = opcoesDoTexto(allCities, basico.cidade);
    const tipos = opcoesDoTexto(allTypes, basico.tipo);
    // 1 cidade no caminho, como sempre; 2+ = caminho sem cidade e `?cidades=<ids>`
    const cidadeLabel = cidades.length === 1 ? cidades[0].label : '';
    const partes = [
        'imoveis',
        String(basico.transacao || 'venda').toLowerCase(),
        cidadeLabel ? encodeSegment(cidadeLabel, codsite) : '-',
        filtros.bairros.length ? filtros.bairros.map((b) => encodeSegment(b.label, codsite)).join('+') : '-',
        filtros.empreendimento?.label ? encodeSegment(filtros.empreendimento.label, codsite) : '-',
        tipos.length ? tipos.map((t) => encodeSegment(t.label, codsite)).join('+') : '-',
    ];
    const codigo = filtros.codigo.trim();
    if (codigo) partes.push(codigo.replaceAll(' ', '%20'));

    const q = new URLSearchParams();
    const min = soNumero(filtros.precoMin);
    const maxBruto = soNumero(filtros.precoMax);
    const max = maxBruto > 0 && maxBruto < min ? min : maxBruto;
    if (min > 0 || max > 0) {
        q.set('min', String(min));
        q.set('max', String(max));
    }
    const areaMin = soNumero(filtros.areaMin);
    const areaMax = soNumero(filtros.areaMax);
    if (areaMin > 0) q.set('area_min', String(areaMin));
    if (areaMax > 0) q.set('area_max', String(areaMax));
    // "2,3" = vários números marcados; um só = o de sempre
    const dorm = qtdTextoParaUrl(basico.dormitorios);
    if (dorm) q.set('dormitorios', dorm);
    const banheiros = qtdTextoParaUrl(filtros.banheiros);
    if (banheiros) q.set('banheiros', banheiros);
    const suites = qtdTextoParaUrl(filtros.suites);
    if (suites) q.set('suites', suites);
    const vagas = qtdTextoParaUrl(filtros.vagas);
    if (vagas) q.set('vagas', vagas);
    if (filtros.categoria) q.set('categoria', String(filtros.categoria));
    if (filtros.datasEntrega.length) q.set('data_entrega', filtros.datasEntrega.join(','));
    if (cidades.length > 1) q.set(CIDADES_QUERY_PARAM, cidades.map((c) => c.value).join(','));

    const vazio = partes.length === 6 && partes.slice(2).every((p) => p === '-') && q.toString() === '';
    let path = `/${partes.join('/')}`;
    if (vazio) path = `${path}?ordem=desc-inclusao`;
    else if (q.toString()) path = `${path}?${virgulaCrua(q.toString())}`;
    return finalize(path);
};

/** Quantos filtros do painel estão em uso (o número do botão da barra). */
export const contarFiltros = (f: FiltrosAvancados): number =>
    [
        f.bairros.length > 0,
        soNumero(f.precoMin) > 0 || soNumero(f.precoMax) > 0,
        soNumero(f.areaMin) > 0 || soNumero(f.areaMax) > 0,
        Boolean(f.suites),
        Boolean(f.banheiros),
        Boolean(f.vagas),
        Boolean(f.categoria),
        f.datasEntrega.length > 0,
        Boolean(f.empreendimento),
        Boolean(f.codigo.trim()),
    ].filter(Boolean).length;

const IconeFechar = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
);
const IconeLupa = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

// Pílulas "Qualquer · 1 · 2 · 3 · 4 · 5+": cada número liga/desliga (2 e 3 quartos); "Qualquer" limpa.
// Número EXATO, como a lateral de /imoveis (o 5 vale "5 ou mais").
const Quantidade: React.FC<{ rotulo: string; valor: string; onChange: (_v: string) => void }> = ({ rotulo, valor, onChange }) => {
    const marcados = qtdListaDoTexto(valor);
    return (
        <div className={styles.grupo}>
            <span className={styles.rotulo} id={`ba-${rotulo}`}>
                {rotulo}
            </span>
            <div className={styles.chips} role="group" aria-labelledby={`ba-${rotulo}`}>
                <button type="button" aria-pressed={!marcados.length} className={`${styles.chip} ${!marcados.length ? styles.chipAtivo : ''}`} onClick={() => onChange('')}>
                    Qualquer
                </button>
                {QTD_NUMEROS.map((n) => {
                    const marcado = marcados.includes(n);
                    return (
                        <button
                            key={n}
                            type="button"
                            aria-pressed={marcado}
                            title={`Marque um ou mais (ex.: 2 e 3 ${rotulo.toLowerCase()})`}
                            className={`${styles.chip} ${marcado ? styles.chipAtivo : ''}`}
                            onClick={() => onChange(alternarQtdTexto(valor, n))}
                        >
                            {n === 5 ? '5+' : n}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const BuscaAvancadaAurora: React.FC<Props> = ({
    aberto,
    onFechar,
    site,
    origin,
    codsite,
    allCities,
    allTypes,
    allCompany,
    basico,
    setDormitorios,
    cidadesNoPainel,
    setCidade,
    finalize,
    onBuscar,
    onContagem,
    corAcao,
    corAcaoTexto,
    corTexto,
    variante,
}) => {
    const [montado, setMontado] = useState(false);
    const [filtros, setFiltros] = useState<FiltrosAvancados>(FILTROS_VAZIOS);
    const [filtroBairro, setFiltroBairro] = useState('');
    const [listaCategorias, setListaCategorias] = useState<OpcaoFiltro[]>([]);
    const [listaDatas, setListaDatas] = useState<OpcaoFiltro[]>([]);
    const [filtroEmp, setFiltroEmp] = useState('');
    const carregouRef = useRef(false);
    const fecharRef = useRef<HTMLButtonElement | null>(null);

    const mostraCategoria = Boolean(site?.categoria_home);
    const mostraDatas = Boolean(site?.mostrar_data_entrega);
    const mostraEmpreendimento = Boolean(site?.exibir_empreendimento) && Array.isArray(allCompany) && allCompany.length > 0;

    useEffect(() => {
        setMontado(true);
    }, []);

    // contagem para o botão da barra
    useEffect(() => {
        onContagem?.(contarFiltros(filtros));
    }, [filtros, onContagem]);

    // ESC fecha, o fundo para de rolar, o foco entra no botão de fechar.
    useEffect(() => {
        if (!aberto || typeof document === 'undefined') return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onFechar();
        };
        const overflowAnterior = document.body.style.overflow;
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        fecharRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflowAnterior;
        };
    }, [aberto, onFechar]);

    // listas do tenant: carregadas uma vez, na primeira abertura (só as que o site mostra).
    useEffect(() => {
        if (!aberto || carregouRef.current) return;
        carregouRef.current = true;
        const cod = codsite || site?.id;
        if (mostraCategoria) {
            categorias(origin, cod)
                .then((r: any) => setListaCategorias(formatSelects(r?.categorias) || []))
                .catch(() => undefined);
        }
        if (mostraDatas) {
            allDatasEntrega(origin, cod)
                .then((r: any) => {
                    const datas = Array.isArray(r?.datas_entrega) ? formatSelectsTransacao(r.datas_entrega) : [];
                    // "Imóvel Pronto" (value 1) na frente, como na busca 12
                    setListaDatas([{ label: 'Imóvel Pronto', value: 1 }, ...datas.filter((d: OpcaoFiltro) => !(d.label === 'Imóvel Pronto' && d.value === 1))]);
                })
                .catch(() => undefined);
        }
    }, [aberto, codsite, site?.id, origin, mostraCategoria, mostraDatas]);

    // bairros acompanham as cidades marcadas na barra: os de todas elas. Mudou a seleção → saem os
    // bairros marcados de cidade que saiu; os que ficam passam ao rótulo da lista nova.
    const { bairros, carregando: carregandoBairros, idsCidades } = useBairrosDasCidades(basico.cidade, origin, codsite || site?.id, allCities);
    useEffect(() => {
        setFiltroBairro('');
        setFiltros((f) => {
            if (!f.bairros.length) return f;
            const ficam = bairros.filter((b) => f.bairros.some((x) => String(x.value) === String(b.value)));
            const igual = ficam.length === f.bairros.length && ficam.every((b) => f.bairros.some((x) => String(x.value) === String(b.value) && x.label === b.label));
            return igual ? f : { ...f, bairros: ficam };
        });
    }, [bairros]);

    const alterar = useCallback(<K extends keyof FiltrosAvancados>(chave: K, valor: FiltrosAvancados[K]) => setFiltros((f) => ({ ...f, [chave]: valor })), []);
    const alternarLista = (chave: 'datasEntrega', valor: string) =>
        setFiltros((f) => {
            const atual = f[chave];
            return { ...f, [chave]: atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor] };
        });
    const alternarBairro = (b: OpcaoFiltro) =>
        setFiltros((f) => {
            const marcado = f.bairros.some((x) => String(x.value) === String(b.value));
            return { ...f, bairros: marcado ? f.bairros.filter((x) => String(x.value) !== String(b.value)) : [...f.bairros, b] };
        });
    const limpar = () => {
        setFiltros(FILTROS_VAZIOS);
        setDormitorios('');
        if (cidadesNoPainel) setCidade?.('');
        setFiltroBairro('');
        setFiltroEmp('');
    };
    const buscar = () => onBuscar(montarUrlAvancada(basico, filtros, allCities, allTypes, finalize, site?.id));

    const bairrosVisiveis = useMemo(() => {
        const termo = normalizaTermo(filtroBairro);
        return termo ? bairros.filter((b) => normalizaTermo(b.label).includes(termo)) : bairros;
    }, [bairros, filtroBairro]);
    const empreendimentosVisiveis = useMemo(() => {
        const termo = normalizaTermo(filtroEmp);
        const lista = (allCompany || []).filter((c) => c?.label);
        return (termo ? lista.filter((c) => normalizaTermo(c.label).includes(termo)) : lista).slice(0, 40);
    }, [allCompany, filtroEmp]);

    const cidadesLabels = opcoesDoTexto(allCities, basico.cidade).map((c) => String(c.label));
    const cidadeLabel = resumoMarcados(cidadesLabels, 'cidades');
    const tipoLabel = resumoMarcados(opcoesDoTexto(allTypes, basico.tipo).map((t) => String(t.label)), 'tipos');
    const resumo = [basico.transacao === 'aluguel' ? 'Aluguel' : basico.transacao === 'temporada' ? 'Temporada' : 'Venda', cidadeLabel || 'Todas as cidades', tipoLabel || 'Todos os tipos'].join(' · ');
    const totalFiltros = contarFiltros(filtros) + (parseInt(basico.dormitorios, 10) > 0 ? 1 : 0) + (cidadesNoPainel && idsCidades.length ? 1 : 0);

    if (!montado || !aberto || typeof document === 'undefined') return null;

    const vars = {
        '--ba-acao': corAcao,
        '--ba-acao-texto': corAcaoTexto,
        '--ba-texto': corTexto,
    } as React.CSSProperties;

    return createPortal(
        <div className={`${styles.veu}${variante === 'horizonte' ? ` ${styles.horizonte}` : variante === 'larissa' ? ` ${styles.larissa}` : variante === 'internacional' ? ` ${styles.internacional}` : ''}`} style={vars} onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
            <div className={styles.painel} role="dialog" aria-modal="true" aria-labelledby="ba-titulo">
                <div className={styles.cabecalho}>
                    <div className={styles.cabecalhoTexto}>
                        <h2 id="ba-titulo" className={styles.titulo}>
                            Busca avançada
                        </h2>
                        <p className={styles.resumo}>{resumo}</p>
                    </div>
                    <button ref={fecharRef} type="button" className={styles.fechar} onClick={onFechar} aria-label="Fechar busca avançada">
                        <IconeFechar />
                    </button>
                </div>

                <div className={styles.corpo}>
                    {/* Larissa: as cidades se marcam aqui (o "Onde?" da barra escolhe um lugar só) */}
                    {cidadesNoPainel && setCidade ? (
                        <div className={styles.grupo}>
                            <span className={styles.rotulo}>Cidades</span>
                            <div className={styles.caixaSelecao}>
                                <CampoSelecaoMulti
                                    opcoes={allCities || []}
                                    valor={basico.cidade}
                                    onChange={setCidade}
                                    rotuloVazio="Todas as cidades"
                                    plural="cidades"
                                    ariaLabel="Cidades"
                                    className={styles.campoSelecao}
                                />
                            </div>
                        </div>
                    ) : null}

                    {/* bairros das cidades marcadas */}
                    <div className={styles.grupo}>
                        <span className={styles.rotulo} id="ba-bairros">
                            {cidadesLabels.length > 1 ? `Bairros das ${cidadesLabels.length} cidades` : `Bairros${cidadeLabel ? ` em ${cidadeLabel}` : ''}`}
                        </span>
                        {!idsCidades.length ? (
                            <p className={styles.dica}>{cidadesNoPainel ? 'Escolha a cidade para filtrar por bairro.' : 'Escolha a cidade na barra de busca para filtrar por bairro.'}</p>
                        ) : (
                            <>
                                {bairros.length > 12 ? (
                                    <input
                                        type="text"
                                        className={styles.input}
                                        placeholder="Filtrar bairros"
                                        aria-label="Filtrar bairros"
                                        value={filtroBairro}
                                        onChange={(e) => setFiltroBairro(e.target.value)}
                                    />
                                ) : null}
                                <div className={`${styles.chips} ${styles.chipsRolavel}`} role="group" aria-labelledby="ba-bairros">
                                    {bairrosVisiveis.map((b) => {
                                        const marcado = filtros.bairros.some((x) => String(x.value) === String(b.value));
                                        return (
                                            <button key={String(b.value)} type="button" aria-pressed={marcado} className={`${styles.chip} ${marcado ? styles.chipAtivo : ''}`} onClick={() => alternarBairro(b)}>
                                                {b.label}
                                            </button>
                                        );
                                    })}
                                    {!bairrosVisiveis.length ? <p className={styles.dica}>{carregandoBairros ? 'Carregando bairros…' : bairros.length ? 'Nenhum bairro com esse nome.' : 'Nenhum bairro cadastrado.'}</p> : null}
                                </div>
                            </>
                        )}
                    </div>

                    {/* preço e área */}
                    <div className={styles.duasColunas}>
                        <div className={styles.grupo}>
                            <span className={styles.rotulo}>Preço</span>
                            <div className={styles.faixa}>
                                <label className={styles.campoCom}>
                                    <span className={styles.prefixo}>R$</span>
                                    <input id="precoMinimoHome" type="text" inputMode="numeric" className={styles.inputNu} placeholder="Mínimo" aria-label="Preço mínimo" value={formataMilhar(filtros.precoMin)} onChange={(e) => alterar('precoMin', e.target.value)} />
                                </label>
                                <span className={styles.ate} aria-hidden="true">
                                    até
                                </span>
                                <label className={styles.campoCom}>
                                    <span className={styles.prefixo}>R$</span>
                                    <input id="precoMaximoHome" type="text" inputMode="numeric" className={styles.inputNu} placeholder="Máximo" aria-label="Preço máximo" value={formataMilhar(filtros.precoMax)} onChange={(e) => alterar('precoMax', e.target.value)} />
                                </label>
                            </div>
                        </div>
                        <div className={styles.grupo}>
                            <span className={styles.rotulo}>Área</span>
                            <div className={styles.faixa}>
                                <label className={styles.campoCom}>
                                    <input type="text" inputMode="numeric" className={styles.inputNu} placeholder="Mínima" aria-label="Área mínima em metros quadrados" value={formataMilhar(filtros.areaMin)} onChange={(e) => alterar('areaMin', e.target.value)} />
                                    <span className={styles.sufixo}>m²</span>
                                </label>
                                <span className={styles.ate} aria-hidden="true">
                                    até
                                </span>
                                <label className={styles.campoCom}>
                                    <input type="text" inputMode="numeric" className={styles.inputNu} placeholder="Máxima" aria-label="Área máxima em metros quadrados" value={formataMilhar(filtros.areaMax)} onChange={(e) => alterar('areaMax', e.target.value)} />
                                    <span className={styles.sufixo}>m²</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* quantidades (vários números) — "Quartos" é o mesmo estado da barra */}
                    <div className={styles.duasColunas}>
                        <Quantidade rotulo="Quartos" valor={basico.dormitorios} onChange={setDormitorios} />
                        <Quantidade rotulo="Suítes" valor={filtros.suites} onChange={(v) => alterar('suites', v)} />
                        <Quantidade rotulo="Banheiros" valor={filtros.banheiros} onChange={(v) => alterar('banheiros', v)} />
                        <Quantidade rotulo="Vagas" valor={filtros.vagas} onChange={(v) => alterar('vagas', v)} />
                    </div>

                    {mostraCategoria && listaCategorias.length ? (
                        <div className={styles.grupo}>
                            <span className={styles.rotulo} id="ba-categoria">
                                Categoria
                            </span>
                            {/* lista suspensa de UMA escolha (Daniel 14/10): "Todas" + as categorias do tenant, no MESMO campo
                                de escolha da barra (CampoSelecao: caixa, chevron, lista estilizada em portal, foco visível) —
                                nada de <select> cru do navegador. A URL segue o mesmo contrato (?categoria=<id>). */}
                            <div className={styles.caixaSelecao}>
                                <CampoSelecao
                                    opcoes={listaCategorias}
                                    valor={filtros.categoria}
                                    onChange={(v) => alterar('categoria', v)}
                                    rotuloVazio="Todas"
                                    ariaLabel="Categoria"
                                    className={styles.campoSelecao}
                                />
                            </div>
                        </div>
                    ) : null}

                    {mostraDatas && listaDatas.length ? (
                        <div className={styles.grupo}>
                            <span className={styles.rotulo} id="ba-entrega">
                                Entrega
                            </span>
                            <div className={styles.chips} role="group" aria-labelledby="ba-entrega">
                                {listaDatas.map((d) => {
                                    const marcado = filtros.datasEntrega.includes(String(d.value));
                                    return (
                                        <button key={String(d.value)} type="button" aria-pressed={marcado} className={`${styles.chip} ${marcado ? styles.chipAtivo : ''}`} onClick={() => alternarLista('datasEntrega', String(d.value))}>
                                            {d.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className={styles.duasColunas}>
                        {mostraEmpreendimento ? (
                            <div className={styles.grupo}>
                                <span className={styles.rotulo} id="ba-emp">
                                    {site?.id == 1892 ? 'Residencial ou loteamento' : 'Condomínio'}
                                </span>
                                {filtros.empreendimento ? (
                                    <div className={styles.chips}>
                                        <button type="button" aria-pressed="true" className={`${styles.chip} ${styles.chipAtivo}`} onClick={() => alterar('empreendimento', null)} title="Remover">
                                            {filtros.empreendimento.label} ✕
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input type="text" className={styles.input} placeholder="Digite o nome" aria-labelledby="ba-emp" value={filtroEmp} onChange={(e) => setFiltroEmp(e.target.value)} />
                                        {filtroEmp.trim() ? (
                                            <div className={`${styles.chips} ${styles.chipsRolavel}`}>
                                                {empreendimentosVisiveis.map((c) => (
                                                    <button key={String(c.value)} type="button" className={styles.chip} onClick={() => { alterar('empreendimento', c); setFiltroEmp(''); }}>
                                                        {c.label}
                                                    </button>
                                                ))}
                                                {!empreendimentosVisiveis.length ? <p className={styles.dica}>Nenhum com esse nome.</p> : null}
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        ) : null}
                        <div className={styles.grupo}>
                            {/* ids codigoDoImovel / precoMinimoHome / precoMaximoHome = os mesmos do hero clássico
                                (WelcomePieces), p/ gatilho de GTM do cliente seguir valendo no modelo novo. O painel é
                                único na página (portal, só aberto) e nenhum hero 13–17 repete esses ids. */}
                            <span className={styles.rotulo} id="ba-codigo">
                                Código do imóvel
                            </span>
                            <input id="codigoDoImovel" type="text" className={styles.input} placeholder="Ex.: 1234" aria-labelledby="ba-codigo" value={filtros.codigo} onChange={(e) => alterar('codigo', e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className={styles.rodape}>
                    <button type="button" className={styles.limpar} onClick={limpar} disabled={!totalFiltros}>
                        Limpar{totalFiltros ? ` (${totalFiltros})` : ''}
                    </button>
                    <button type="button" className={styles.buscar} onClick={buscar}>
                        <IconeLupa />
                        Buscar imóveis
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default BuscaAvancadaAurora;
